import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Flag, ShieldCheck } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError("Missing reset token. Open the link from your email.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (pw !== confirm) { setError("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-flag-ivory flex flex-col">
      <div className="stripe-accent" />
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 justify-center font-display font-black text-2xl text-flag-navy mb-6" data-testid="reset-logo">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-flag-red text-white">
              <Flag className="h-4 w-4" />
            </span>
            Electable
          </Link>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8">
            {!done ? (
              <>
                <h1 className="font-display text-2xl font-bold text-flag-navy">Set a new password</h1>
                <p className="mt-1 text-slate-600">Single-use link. Expires 1 hour after it was created.</p>
                <form onSubmit={onSubmit} className="mt-6 space-y-4">
                  <div>
                    <Label htmlFor="new-pw" className="text-sm font-semibold text-slate-800">New password</Label>
                    <PasswordInput id="new-pw" autoComplete="new-password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-new-password-input" className="mt-1 h-12 text-base" placeholder="At least 6 characters" />
                  </div>
                  <div>
                    <Label htmlFor="confirm-pw" className="text-sm font-semibold text-slate-800">Confirm new password</Label>
                    <PasswordInput id="confirm-pw" autoComplete="new-password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-password-input" className="mt-1 h-12 text-base" />
                  </div>
                  {error && (
                    <div className="text-sm text-flag-red bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="reset-error">{error}</div>
                  )}
                  <Button type="submit" disabled={loading || !token} data-testid="reset-submit-btn" className="w-full h-12 bg-flag-red hover:bg-flag-red-hover text-white font-bold text-base">
                    {loading ? "Updating…" : "Set new password"}
                  </Button>
                </form>
              </>
            ) : (
              <div data-testid="reset-done" className="text-center">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-800 mx-auto">
                  <ShieldCheck className="h-6 w-6" />
                </div>
                <h1 className="font-display text-2xl font-bold text-flag-navy mt-4">Password updated</h1>
                <p className="mt-2 text-slate-600">Redirecting you to sign in…</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
